export type RegistryPaymentLike = {
  paymentType?: string | null;
  obligation?: { direction?: string | null; stage?: string | null } | null;
};

/**
 * Колонка «Тип» (реестр): откуда деньги / направление потока (не предоплата vs доплата).
 */
export const PAYMENT_FLOW_LABELS: Record<string, string> = {
  APPLICATION: 'Поступление от клиента',
  PARTNER_PAYMENT: 'Оплата перевозчику',
  ROUTE_PAYMENT: 'Оплата перевозчику',
  PARTNER_RECEIPT: 'Поступление от партнёра',
  ADJUSTMENT: 'Корректировка',
};

/**
 * Колонка «Назначение»: этап обязательства (за что платят).
 */
export const PAYMENT_STAGE_LABELS: Record<string, string> = {
  PREPAYMENT: 'Предоплата',
  FINAL: 'Доплата',
};

export type PaymentFlowLike = RegistryPaymentLike & {
  applicationId?: number | null;
  routeId?: number | null;
  allocations?: Array<{ obligation?: { stage?: string | null; direction?: string | null } | null }>;
};

/** Направление денег для колонки «Тип». */
export function getPaymentFlowLabel(payment: PaymentFlowLike): string {
  const dir = payment.obligation?.direction;
  if (dir === 'INCOME') return 'Поступление от клиента';
  if (dir === 'EXPENSE') return 'Оплата перевозчику';

  const raw = payment.paymentType != null ? String(payment.paymentType) : '';
  if (PAYMENT_FLOW_LABELS[raw]) return PAYMENT_FLOW_LABELS[raw];

  if (raw === 'PREPAYMENT' || raw === 'FINAL_PAYMENT') {
    const appId = payment.applicationId;
    const routeId = payment.routeId;
    if (appId != null && routeId == null) return 'Поступление от клиента';
    return 'Оплата перевозчику';
  }

  return raw || '—';
}

/** Цвет тега «Тип» (поток). */
export function getPaymentFlowTagColor(payment: PaymentFlowLike): string {
  const label = getPaymentFlowLabel(payment);
  if (label === 'Поступление от клиента' || label === 'Поступление от партнёра') return 'green';
  if (label === 'Оплата перевозчику') return 'orange';
  if (label === 'Корректировка') return 'purple';
  return 'default';
}

/**
 * Этап для колонки «Назначение»: obligation → allocations → paymentType.
 */
export function getRegistryPaymentPurposeStage(payment: PaymentFlowLike): string | null {
  const oblStage = payment.obligation?.stage;
  if (oblStage) return String(oblStage);

  const allocs = payment.allocations;
  if (Array.isArray(allocs) && allocs.length > 0) {
    const stages = allocs
      .map((a) => a.obligation?.stage)
      .filter((s): s is string => s != null && String(s) !== '');
    const uniq = Array.from(new Set(stages.map(String)));
    if (uniq.length === 1) return uniq[0];
    if (uniq.length > 1) return 'MIXED';
  }

  const pt = payment.paymentType != null ? String(payment.paymentType) : '';
  if (pt === 'PREPAYMENT') return 'PREPAYMENT';
  if (pt === 'FINAL_PAYMENT') return 'FINAL';
  return null;
}

export function getPaymentPurposeLabel(payment: PaymentFlowLike): string {
  const stage = getRegistryPaymentPurposeStage(payment);
  if (!stage) return '—';
  if (stage === 'MIXED') return 'Несколько этапов';
  return PAYMENT_STAGE_LABELS[stage] || stage;
}

/** Тег назначения: предоплата — оранжевый, доплата — синий. */
export function getPaymentPurposeTagColor(payment: PaymentFlowLike): string {
  const stage = getRegistryPaymentPurposeStage(payment);
  if (stage === 'PREPAYMENT') return 'orange';
  if (stage === 'FINAL') return 'blue';
  return 'default';
}

/**
 * Человекочитаемые подписи для paymentType (реестр платежей и прочие таблицы).
 */
export const PAYMENT_TYPE_LABELS: Record<string, string> = {
  APPLICATION: 'Поступление от клиента',
  PARTNER_PAYMENT: 'Оплата перевозчику',
  ROUTE_PAYMENT: 'Оплата перевозчику',
  PARTNER_RECEIPT: 'Поступление от партнёра',
  ADJUSTMENT: 'Корректировка',
  PREPAYMENT: 'Предоплата',
  FINAL_PAYMENT: 'Доплата',
};

/** Подпись для ячейки таблицы: сначала тип платежа, затем направление обязательства. */
export function getRegistryPaymentTypeLabel(payment: RegistryPaymentLike): string {
  const raw = payment.paymentType != null ? String(payment.paymentType) : '';
  if (raw && PAYMENT_TYPE_LABELS[raw]) {
    return PAYMENT_TYPE_LABELS[raw];
  }
  const dir = payment.obligation?.direction;
  if (dir === 'INCOME') return 'Поступление от клиента';
  if (dir === 'EXPENSE') return 'Оплата перевозчику';
  return raw || '—';
}

/**
 * Цвет Tag: поступления — зелёный, исходящие по маршруту/перевозчику — оранжевый.
 */
export function getRegistryPaymentTypeTagColor(payment: RegistryPaymentLike): string {
  const dir = payment.obligation?.direction;
  if (dir === 'INCOME') return 'green';
  if (dir === 'EXPENSE') return 'orange';

  const t = payment.paymentType != null ? String(payment.paymentType) : '';
  if (t === 'APPLICATION' || t === 'PARTNER_RECEIPT') return 'green';
  if (
    t === 'PREPAYMENT' ||
    t === 'FINAL_PAYMENT' ||
    t === 'PARTNER_PAYMENT' ||
    t === 'ROUTE_PAYMENT'
  ) {
    return 'orange';
  }
  if (t === 'ADJUSTMENT') return 'purple';
  return 'default';
}
